import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { AutomatedSurveyEditRequest, AutomatedSurveyRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { automatedSurveyMutations } from "@/queries/automated-surveys/mutations";
import { useAutomatedSurveyQueries } from "@/queries/automated-surveys/queryOptions";
import { routes } from "@/routes";

import { automatedSurveyQuestionCategories } from "../../constants";
import { AutomatedSurveyForm, type FormValues } from "./AutomatedSurveyForm";

export function EditAutomatedSurveyPage(): React.ReactNode {
  const navigate = useNavigate();
  const { id: surveyId } = useParams<{ id: string }>();
  const queries = useAutomatedSurveyQueries();
  const detailsQuery = useQuery(queries.details(surveyId!));
  const editAutomatedSurvey = automatedSurveyMutations.useEditAutomatedSurvey();

  if (detailsQuery.error) {
    return <ErrorPage error={detailsQuery.error} />;
  }
  if (detailsQuery.isFetching || !detailsQuery.data) {
    return <FullSizeLoader withPadding />;
  }

  const details = detailsQuery.data;
  const defaultFormValues = {
    name: details.title,
    description: details.translations?.[0]?.description || "",
    thanksMessage: details.translations?.[0]?.thankYouMessage || "",
    interval: details.weeklyInterval.toString(),
    minSampleSize: details.usersSampleSize.toString(),
    type: details.category,
    projects: details.enabledProjects,
    active: details.isLocked,
    questions: details.questionGroups.map((question) => ({
      qId: question.id,
      category: automatedSurveyQuestionCategories.find((category) => category.id === question.type)!,
      ratingQuestion: question.translations?.[0]?.ratingQuestionTemplate || "",
      openQuestion: question.translations?.[0]?.openQuestionTemplate || "",
      explanation: question.translations?.[0]?.explanation || "",
    })),
  } satisfies FormValues;

  const handleSubmit = (data: AutomatedSurveyEditRequest | AutomatedSurveyRequest) =>
    editAutomatedSurvey.mutateAsync(
      { surveyId: surveyId!, data: data as AutomatedSurveyEditRequest },
      {
        onSuccess: () => {
          void navigate(routes.automatedSurveys.list());
        },
      },
    );

  return (
    <AutomatedSurveyForm
      mode="edit"
      surveyId={surveyId}
      defaultFormValues={defaultFormValues}
      isSubmitting={editAutomatedSurvey.isPending}
      onSubmit={handleSubmit}
    />
  );
}
